import { memo, useCallback } from 'react';

import { QuantityInput } from '~/features/products/components/ProductDetails/components/QuantityInput/QuantityInput';
import Check from '~/icons/check.svg';
import { ILineItem, ILink, IVariationDetails } from '~/lib/data-contract';
import { Button } from '~/shared/components/Button/Button';
import { ButtonGroup } from '~/shared/components/ButtonGroup/ButtonGroup';
import { DialogFooter } from '~/shared/components/Dialog/components/DialogFooter/DialogFooter';
import { Icon } from '~/shared/components/Icon/Icon';
import Link from '~/shared/components/Link/Link';
import { LoadingCircle } from '~/shared/components/Loading/LoadingCircle';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import styles from './addToBasketPane.module.css';

import { useBasket } from '../../hooks/useBasket';
import UseBasketPaneState from '../../hooks/useBasketPaneState';
import { LineItem } from '../LineItem/LineItem';
import { UpsellProducts } from '../UpsellProducts';

type PaneContentProps = {
	isLoading?: boolean;
	errorMessage?: string;
	lineItem: ILineItem;
	basketLink?: ILink;
	onQuantityChange: (quantity: number) => void;
};

function PaneContentComponent({ isLoading, errorMessage, lineItem, basketLink, onQuantityChange }: PaneContentProps) {
	const { closeDialog } = UseBasketPaneState();
	const { translate } = useTranslation();
	const { addItem } = useBasket();
	const { url = '#', ...linkProps } = basketLink || { url: '#' };

	const handleUpsellAdd = useCallback(
		(variationDetails: IVariationDetails) => {
			addItem({
				variation: variationDetails,
				quantity: 1,
			});
		},
		[addItem],
	);

	return (
		<div className={styles.root}>
			<h3 className={styles.title}>
				<Icon
					className={styles.icon}
					size="xl"
				>
					{isLoading ? <LoadingCircle /> : <Check />}
				</Icon>
				{translate('basket.item-added')}
				{!isLoading && errorMessage ? <div className={styles.error}>{errorMessage}</div> : null}
			</h3>
			{!isLoading ? (
				<>
					<div className={styles.lineItemWrap}>
						<LineItem
							as="div"
							{...lineItem}
							labelType="series"
						/>
						{!lineItem.isGift ? (
							<QuantityInput
								style={{ marginLeft: 'auto' }}
								value={lineItem.quantity}
								onChange={onQuantityChange}
								size="xs"
							/>
						) : null}
					</div>

					<UpsellProducts onUpsellAdd={handleUpsellAdd} />
				</>
			) : null}
			<DialogFooter>
				<ButtonGroup>
					<Button
						variant="outline"
						onClick={closeDialog}
					>
						{translate('basket.continue-shopping')}
					</Button>
					<Button
						as={Link}
						href={url}
						{...linkProps}
					>
						{translate('basket.go-to-checkout')}
					</Button>
				</ButtonGroup>
			</DialogFooter>
		</div>
	);
}

export const PaneContent = memo(PaneContentComponent);
